/**
 * Shared OCR-confidence helper for Dex live scan (b17a).
 *
 * b17a turns the live scan into a bidirectional reconciliation engine: as well
 * as additively marking caught entries, it surfaces CONFLICTS when the scan
 * reads a cell as NOT caught while the user has manually marked it caught. That
 * un-catch suggestion is risky — a false positive could wipe legitimate manual
 * data — so it is gated strictly on OCR confidence.
 *
 * ── Single source of confidence (calibration dependency on b16) ─────────────
 *
 * b16 is in flight retuning small-sprite caught-detection. To make sure b17a
 * picks up that retuning automatically rather than drifting out of sync, EVERY
 * confidence read in b17a flows through `frameConfidence()` here, and the
 * HIGH/MED/LOW band cutoffs live in ONE place (`CONFIDENCE_THRESHOLDS`). When
 * b16 lands, the band cutoffs can be re-tuned in this file alone — see the PR
 * description's calibration note. No confidence math is hard-coded at the call
 * sites.
 *
 * The confidence number itself is the per-frame OCR confidence (0-100) the
 * pipeline already computes (`PokedexGridFrame.confidence`, surfaced on the
 * adapter result as `DexLiveFrameResult.confidence`). That is the same signal
 * the frame gate trusts (`GATE_MIN_CONFIDENCE`), so b16's tuning of the
 * upstream confidence estimate flows straight through.
 */

/** Coarse confidence band a reading falls into. */
export type ConfidenceBand = "high" | "medium" | "low";

/**
 * Sensitivity profile for conflict (un-catch) surfacing, chosen by the user in
 * Settings → Scan handling. Higher cutoffs mean fewer false positives but fewer
 * suggestions; lower cutoffs mean more suggestions.
 */
export type ConfidenceProfile = "standard" | "conservative" | "aggressive";

/**
 * HIGH/MED/LOW cutoffs per profile, expressed as the minimum frame confidence
 * (0-100) needed to reach that band. A reading is HIGH when `confidence >=
 * high`, MEDIUM when `>= medium`, otherwise LOW.
 *
 * The "standard" HIGH cutoff (70) sits deliberately above the frame gate's
 * trust floor (`GATE_MIN_CONFIDENCE = 55`): a frame can be trusted enough to
 * ADD a caught entry at 55, but un-catching a manually-caught entry demands a
 * cleaner read. Conservative raises it further; Aggressive lowers it toward the
 * gate floor so more borderline reads can suggest a gap.
 *
 * SINGLE SOURCE OF TRUTH — re-tune here (and only here) once b16's small-sprite
 * calibration lands.
 */
export const CONFIDENCE_THRESHOLDS: Record<
  ConfidenceProfile,
  { high: number; medium: number }
> = {
  // Standard = current sensible HIGH cutoff.
  standard: { high: 70, medium: 50 },
  // Conservative = higher cutoff → fewer false-positive un-catch suggestions.
  conservative: { high: 82, medium: 55 },
  // Aggressive = lower cutoff → more gap-finding (closer to the gate floor).
  aggressive: { high: 60, medium: 45 },
};

export const DEFAULT_CONFIDENCE_PROFILE: ConfidenceProfile = "standard";

/**
 * The single confidence read used throughout b17a. Takes anything carrying a
 * numeric `confidence` (the adapter result, a raw frame, or a session total)
 * and returns a clamped 0-100 number. Centralising the read is what lets b16's
 * upstream confidence tuning flow through without touching reconciliation code.
 */
export function frameConfidence(source: { confidence?: number } | null | undefined): number {
  const raw = source?.confidence;
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 0;
  return Math.max(0, Math.min(100, raw));
}

/** Classify a 0-100 confidence into a band under the given profile. */
export function confidenceBand(
  confidence: number,
  profile: ConfidenceProfile = DEFAULT_CONFIDENCE_PROFILE,
): ConfidenceBand {
  const t = CONFIDENCE_THRESHOLDS[profile] ?? CONFIDENCE_THRESHOLDS[DEFAULT_CONFIDENCE_PROFILE];
  const c = Math.max(0, Math.min(100, confidence));
  if (c >= t.high) return "high";
  if (c >= t.medium) return "medium";
  return "low";
}

/**
 * Whether a reading clears the HIGH bar for the profile — the gate for
 * surfacing an un-catch conflict. This is the ONLY predicate reconciliation
 * uses to decide a "not caught vs manual caught" disagreement is trustworthy
 * enough to show the user.
 */
export function isHighConfidence(
  confidence: number,
  profile: ConfidenceProfile = DEFAULT_CONFIDENCE_PROFILE,
): boolean {
  return confidenceBand(confidence, profile) === "high";
}
